import { queryClient } from '@/plugins/queryClient';
import { RouteNames } from '@/router/routeNames';
import useAuthStore from '@/stores/useAuthStore';
import useCreditNoteStore from '@/stores/useCreditNoteStore';
import useLocationsStore from '@/stores/useLocationsStore';
import useOrderStore from '@/stores/useOrderStore';
import { setRedirectAfterLogin } from '@/utils/localStorageUtil';
import { storeToRefs } from 'pinia';
import type { NavigationGuard, NavigationGuardReturn, RouteLocationNormalized } from 'vue-router';

const initializeStoresForLocationCheck = () => {
    useLocationsStore();
    useOrderStore();
    useCreditNoteStore();
};

const checkLocation = (to: RouteLocationNormalized) => {
    if (to.name === RouteNames.Index || to.path === '/') {
        return true;
    }

    const { selected } = storeToRefs(useLocationsStore());

    if (to.name === RouteNames.ToProcess) {
        return !!selected.value?.length || { name: RouteNames.Index };
    }

    // Do not block navigation while route-related queries are still loading.
    // Target views already handle loading and error states.
    if (queryClient.isFetching() > 0) {
        return true;
    }

    const { hasProcessedOrders } = storeToRefs(useOrderStore());

    const canAccessByRoute: Partial<Record<RouteNames, () => boolean>> = {
        [RouteNames.Processed]: () => !!hasProcessedOrders.value,
    };

    const routeName = typeof to.name === 'string' ? (to.name as RouteNames) : undefined;

    // Routes that are not explicitly gated should be accessible.
    // This prevents unintended redirects for routes like credit notes.
    if (!routeName || !canAccessByRoute[routeName]) {
        return true;
    }

    return canAccessByRoute[routeName]() || { name: RouteNames.Index };
};

const authGuard =
    (): NavigationGuard =>
    async (to: RouteLocationNormalized): Promise<NavigationGuardReturn> => {
        if (to.meta.requiresAuth === false) {
            return true;
        }

        const { trySilentLogins, getToken, loadUserRole } = useAuthStore();
        let isAuthenticated = !!(await getToken());

        if (!isAuthenticated) {
            isAuthenticated = !!(await trySilentLogins());

            if (!isAuthenticated) {
                setRedirectAfterLogin(to.fullPath);
                return { name: RouteNames.Index };
            }
        }

        await loadUserRole();

        if (to.name !== RouteNames.Index && to.path !== '/') {
            initializeStoresForLocationCheck();
        }

        return checkLocation(to);
    };

export default authGuard;
